import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { getLocalizedPath } from '@/lib/routes';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { staggerContainer, scaleIn } from '@/hooks/useScrollAnimation';
import type { Project } from '../../shared/schema';

const categoryLabels: Record<string, Record<string, string>> = {
  'Cultural Events': { en: 'Cultural Events', ar: 'فعاليات ثقافية' },
  'Celebrations': { en: 'Celebrations', ar: 'الاحتفالات' },
  'TV Broadcasting & Media Coverage': { en: 'TV Broadcasting & Media Coverage', ar: 'النقل التلفزيوني والتغطية الإعلامية' },
  'Business Events': { en: 'Business Events', ar: 'فعاليات الاعمال' },
  'Sports Events': { en: 'Sports Events', ar: 'فعاليات رياضية' },
};

export const Achievements = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const { data: projects = [], isLoading, isError, refetch } = useQuery<Project[]>({
    queryKey: ['/api/projects'],
  });

  const featured = projects.filter(p => p.featured).slice(0, 3);
  const displayProjects = featured.length > 0 ? featured : projects.slice(0, 3);

  const getTitle = (p: Project) => lang === 'ar' ? p.titleAr : p.titleEn;
  const getSlug = (p: Project) => lang === 'ar' ? p.slugAr : p.slugEn;

  return (
    <section id="portfolio" className="py-24 lg:py-32 bg-background relative overflow-hidden">

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={staggerContainer}
          className="mb-16">
          <motion.div variants={scaleIn} className="flex items-center gap-4 text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-tight mb-2 text-foreground">
            <img src="/title-icon.png" alt="" className="w-8 h-8 md:w-10 md:h-10" />
            {t('achievements.label')}
          </motion.div>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <p className="text-muted-foreground">{t('common.loadError', 'Failed to load data')}</p>
            <button onClick={() => refetch()} data-testid="button-retry-achievements" className="px-6 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">{t('common.retry', 'Try Again')}</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {displayProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                category={`${categoryLabels[project.category]?.[lang] || project.category} / ${project.year}`}
                title={getTitle(project)}
                image={project.image}
                slug={getSlug(project)}
                index={index}
              />
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-center mt-12">
          <Link to={getLocalizedPath('projects', lang)}>
            <motion.span
              className="btn-outline inline-flex items-center gap-2"
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px hsl(290 46% 49% / 0.3)' }}
              whileTap={{ scale: 0.98 }}>
              {t('achievements.viewAll')} <ArrowRight className="w-4 h-4" />
            </motion.span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const ProjectCard = ({
  category,
  title,
  image,
  slug,
  index,
}: { category: string; title: string; image: string; slug: string; index: number }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const cardRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -50]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.95]);
  const rotate = useTransform(scrollYProgress, [0, 0.5, 1], [2, 0, -2]);

  return (
    <Link to={`${getLocalizedPath('projects', lang)}/${slug}`} data-testid={`card-featured-project-${index}`}>
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 80, rotateX: 15 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 1.2, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ y, scale, rotate, transformPerspective: 1000 }}
        whileHover={{
          y: -15,
          scale: 1.02,
          transition: { duration: 0.3 }
        }}
        className="group cursor-pointer">
        <div className="relative aspect-[3/4] overflow-hidden rounded-xl mb-4 gradient-border">
          <div className="absolute inset-0 shimmer bg-card" />
          <motion.img
            src={image}
            alt={title}
            className="w-full h-full object-cover relative z-10 grayscale transition-all duration-700 group-hover:grayscale-0"
            whileHover={{ scale: 1.15 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} />
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent z-20"
            initial={{ opacity: 0.6 }}
            whileHover={{ opacity: 0.9 }}
            transition={{ duration: 0.5 }} />
          <div className="absolute bottom-6 left-6 right-6 z-30">
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + index * 0.15, duration: 0.8 }}
              className="text-primary text-sm font-medium mb-2">
              {category}
            </motion.p>
            <motion.h3
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + index * 0.15, duration: 0.8 }}
              className="text-xl font-bold text-foreground group-hover:text-gradient transition-all duration-300">
              {title}
            </motion.h3>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -45 }}
            whileHover={{ opacity: 1, scale: 1, rotate: 0 }}
            className="absolute top-6 right-6 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <motion.div
              className="w-12 h-12 rounded-full glass-card flex items-center justify-center glow-primary"
              whileHover={{ rotate: 45 }}>
              <ArrowRight className="w-5 h-5 text-primary" />
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </Link>
  );
};
